import {
  GAME_RESULT_VARIANT,
  GAME_SESSION_STATUS,
  LIVE_REACHED_END_RECOVERY_RESULT,
} from '@/constants/maze';
import { runLiveReachedEndRecoveryIfNeeded } from '@/section/maze/live-reached-end-recovery';
import {
  delay,
  isTerminalGameStatus,
  LIVE_GAME_BOOTSTRAP_MIN_UI_MS,
  resolveRoundsProgress,
} from '@/section/maze/maze-live-session-utils';
import type { LiveGameResult } from '@/store/live';
import { useLiveStore } from '@/store/live';
import type { GameSessionResponse } from '@/types/api/game';
import type { StageId } from '@/types/stage-theme';
import { normalizeStageId } from '@/utils/stage/stage-utils';
import { paths } from '@app/router/routes';
import { useEffect } from 'react';
import type { NavigateFunction } from 'react-router-dom';

/** Ref-like flags the hook mutates; avoids deprecated `MutableRefObject` in public params. */
export interface WritableBooleanRef {
  current: boolean;
}

export interface UseLiveGameBootstrapParams {
  readonly isDemo: boolean;
  readonly isPreview: boolean;
  readonly stageId: StageId;
  readonly suppressLiveStatusBootstrapRef: WritableBooleanRef;
  readonly liveBootstrapFinishedRef: WritableBooleanRef;
  readonly setLiveBootstrapUiReady: (ready: boolean) => void;
  readonly applyLiveSessionToHud: (data: GameSessionResponse) => void;
  readonly fetchStatus: () => Promise<GameSessionResponse | null>;
  readonly clearLiveSession: () => void;
  readonly navigate: NavigateFunction;
  readonly setResult: (payload: LiveGameResult) => void;
  readonly advanceRoundAfterReachingEnd: ReturnType<
    typeof useLiveStore.getState
  >['advanceRoundAfterReachingEnd'];
}

export function useLiveGameBootstrap(params: UseLiveGameBootstrapParams): void {
  const {
    isDemo,
    isPreview,
    stageId,
    suppressLiveStatusBootstrapRef,
    liveBootstrapFinishedRef,
    setLiveBootstrapUiReady,
    applyLiveSessionToHud,
    fetchStatus,
    clearLiveSession,
    navigate,
    setResult,
    advanceRoundAfterReachingEnd,
  } = params;

  useEffect(() => {
    if (isDemo || isPreview) {
      return;
    }

    if (suppressLiveStatusBootstrapRef.current || liveBootstrapFinishedRef.current) {
      return;
    }

    let cancelled = false;

    const finishBootstrap = () => {
      liveBootstrapFinishedRef.current = true;
      setLiveBootstrapUiReady(true);
    };

    const markBootstrapFinishedForTerminalNav = () => {
      liveBootstrapFinishedRef.current = true;
    };

    const bootstrapLive = async () => {
      if (suppressLiveStatusBootstrapRef.current || liveBootstrapFinishedRef.current) {
        return;
      }
      if (cancelled) {
        return;
      }

      const startedAt = Date.now();

      const cached = useLiveStore.getState();
      if (cached.hasActiveLiveSession && cached.session?.status === GAME_SESSION_STATUS.STARTED) {
        applyLiveSessionToHud(cached.session);
        finishBootstrap();
        return;
      }

      try {
        const data = await fetchStatus();
        if (cancelled) {
          return;
        }
        if (!data) {
          const afterNull = useLiveStore.getState();
          if (
            afterNull.hasActiveLiveSession &&
            afterNull.session?.status === GAME_SESSION_STATUS.STARTED
          ) {
            applyLiveSessionToHud(afterNull.session);
            finishBootstrap();
            return;
          }
          finishBootstrap();
          if (!cancelled) {
            clearLiveSession();
            navigate(paths.home, { replace: true });
          }
          return;
        }
        if (isTerminalGameStatus(data.status)) {
          const elapsed = Date.now() - startedAt;
          await delay(Math.max(0, LIVE_GAME_BOOTSTRAP_MIN_UI_MS - elapsed));
          if (cancelled) {
            return;
          }
          liveBootstrapFinishedRef.current = true;
          const stage = normalizeStageId(stageId);
          const { total, completed } = resolveRoundsProgress(data);
          const score = data.scoreboard.totalScore;
          const resolvedVariant =
            score === 0 ? GAME_RESULT_VARIANT.FAILURE : GAME_RESULT_VARIANT.SUCCESS;
          setResult({
            stage,
            score,
            completed,
            total,
            variant: resolvedVariant,
          });
          navigate(paths.results, { replace: true });
          return;
        }

        try {
          const recovery = await runLiveReachedEndRecoveryIfNeeded(data, {
            cancelled: () => cancelled,
            startedAt,
            stageId,
            advanceRoundAfterReachingEnd,
            applyLiveSessionToHud,
            finishBootstrap,
            markBootstrapFinishedForTerminalNav,
            setLiveBootstrapUiReady,
            clearLiveSession,
            navigate,
            setResult,
          });
          if (cancelled) {
            return;
          }
          if (recovery === LIVE_REACHED_END_RECOVERY_RESULT.HANDLED) {
            return;
          }
        } catch {
          if (cancelled) {
            return;
          }
          finishBootstrap();
          const afterError = useLiveStore.getState();
          if (
            afterError.hasActiveLiveSession &&
            afterError.session?.status === GAME_SESSION_STATUS.STARTED
          ) {
            applyLiveSessionToHud(afterError.session);
            return;
          }
          clearLiveSession();
          navigate(paths.home, { replace: true });
        }

        if (cancelled) {
          return;
        }
        applyLiveSessionToHud(data);
        finishBootstrap();
      } catch {
        if (cancelled) {
          return;
        }
        finishBootstrap();
        const afterError = useLiveStore.getState();
        if (
          afterError.hasActiveLiveSession &&
          afterError.session?.status === GAME_SESSION_STATUS.STARTED
        ) {
          applyLiveSessionToHud(afterError.session);
          return;
        }
        clearLiveSession();
        navigate(paths.home, { replace: true });
      }
    };

    void bootstrapLive();

    return () => {
      cancelled = true;
    };
  }, [
    advanceRoundAfterReachingEnd,
    applyLiveSessionToHud,
    clearLiveSession,
    fetchStatus,
    isDemo,
    isPreview,
    liveBootstrapFinishedRef,
    navigate,
    setLiveBootstrapUiReady,
    setResult,
    stageId,
    suppressLiveStatusBootstrapRef,
  ]);
}
